import { extent } from 'd3-array';
import { axisLeft, axisRight } from 'd3-axis';
import { scaleLinear } from 'd3-scale';

import { getAxisExtent } from './get-axis-extent';
import { calculateTickValues } from './calculate-tick-values';
import { renderPanelYAxis } from './render-panel-y-axis';
import { renderPanelGridlines } from './render-panel-gridlines';
import { renderPanelLegend } from './render-panel-legend';
import { renderSeries } from './render-series';
import { renderPanelBaseline } from './render-panel-baseline';
import { renderPanelPlotBands } from './render-panel-plot-bands';
import { renderPanelPlotLines } from './render-panel-plot-lines';
import { yAxis } from '../components/axis-y';

export function renderPanel(parentElem, graph, panel, panelIndex, panelLayout, xPadding, xScale, defs) {

	defs.append('clipPath')
		.attr('id', 'hw_panel_graph_clip_path_' + panelIndex)
		.append('rect')
		.attr('x', panelLayout.x - xPadding)
		.attr('y', panelLayout.y)
		.attr('width', panelLayout.width + 2 * xPadding)
		.attr('height', panelLayout.height);

	const panelElem = parentElem.append('g')
		.attr('class', 'hw_panel_graph_panel');

	panelElem.append('rect')
		.attr('class', 'hw_panel_graph_panel_bg')
		.attr('x', panelLayout.x)
		.attr('y', panelLayout.y)
		.attr('width', panelLayout.width)
		.attr('height', panelLayout.height);

	// Make sure to render plotlines behind the data.
	renderPanelPlotBands(panelElem, panelLayout, xPadding, graph.config.plotBands, xScale);
	renderPanelPlotLines(panelElem, panelLayout, panelIndex, graph.config.plotLines, xScale);

	const clippedRegion = panelElem.append('g')
		.attr('clip-path', 'url(#hw_panel_graph_clip_path_' + panelIndex + ')');

	renderPanelBaseline(panelElem, panelLayout, xPadding);

	if (panel.config.axisDual === null && panel.config.axisLeft === null && panel.config.axisRight === null) {
		// Default to a dual axis
		panel.axisDual(yAxis());
	}

	let tickValuesLeft, tickValuesRight;
	let scaleLeft, scaleRight;
	let tickFormatterLeft, tickFormatterRight;

	let panelAxisLeft = null;
	let panelAxisRight = null;

	if (panel.config.axisDual) {

		tickValuesLeft = tickValuesRight = getTickValues(
			panel.config.axisDual,
			null,
			panelLayout.height,
			getAxisExtent(graph, panel, null)
		);

		scaleLeft = scaleRight = getScale(panelLayout, tickValuesLeft);

		tickFormatterLeft = panel.config.axisDual.config.formatters[0];
		tickFormatterRight = panel.config.axisDual.config.formatters[1] || tickFormatterLeft;

		panelAxisLeft = getD3Axis('left', tickValuesLeft, scaleLeft, tickFormatterLeft);
		panelAxisRight = getD3Axis('right', tickValuesRight, scaleRight, tickFormatterRight);

	} else if (panel.config.axisLeft || panel.config.axisRight) {

		let primaryAxis;

		if (!panel.config.axisRight) {
			primaryAxis = 'left';
		} else if (!panel.config.axisLeft) {
			primaryAxis = 'right';
		} else {
			primaryAxis = panel.config.axisRight.config.isPrimary ? 'right' : 'left';
		}

		if (primaryAxis === 'left') {

			tickValuesLeft = getTickValues(
				panel.config.axisLeft,
				'left',
				panelLayout.height,
				getAxisExtent(graph, panel, 'left')
			);

			scaleLeft = getScale(panelLayout, tickValuesLeft);
			tickFormatterLeft = panel.config.axisLeft.config.formatters[0];
			panelAxisLeft = getD3Axis('left', tickValuesLeft, scaleLeft, tickFormatterLeft);

			if (panel.config.axisRight) {

				// Get tick values for right axis with matching number of ticks as the left axis
				tickValuesRight = getTickValues(
					panel.config.axisRight,
					'right',
					panelLayout.height,
					getAxisExtent(graph, panel, 'right'),
					tickValuesLeft.length
				);

				scaleRight = getScale(panelLayout, tickValuesRight);
				tickFormatterRight = panel.config.axisRight.config.formatters[0];
				panelAxisRight = getD3Axis('right', tickValuesRight, scaleRight, tickFormatterRight);

			}

		} else {

			tickValuesRight = getTickValues(
				panel.config.axisRight,
				'right',
				panelLayout.height,
				getAxisExtent(graph, panel, 'right')
			);

			scaleRight = getScale(panelLayout, tickValuesRight);
			tickFormatterRight = panel.config.axisRight.config.formatters[0];
			panelAxisRight = getD3Axis('right', tickValuesRight, scaleRight, tickFormatterRight);

			if (panel.config.axisLeft) {

				// Get tick values for left axis with matching number of ticks as the right axis
				tickValuesLeft = getTickValues(
					panel.config.axisLeft,
					'left',
					panelLayout.height,
					getAxisExtent(graph, panel, 'left'),
					tickValuesRight.length
				);

				scaleLeft = getScale(panelLayout, tickValuesLeft);
				tickFormatterLeft = panel.config.axisLeft.config.formatters[0];
				panelAxisLeft = getD3Axis('left', tickValuesLeft, scaleLeft, tickFormatterLeft);

			}

		}

	}

	renderPanelGridlines(clippedRegion, tickValuesLeft, scaleLeft, panelLayout, xPadding);

	if (panelAxisLeft !== null) {
		renderPanelYAxis(panelElem, panelAxisLeft, panelLayout.x - xPadding);
	}

	if (panelAxisRight !== null) {
		renderPanelYAxis(panelElem, panelAxisRight, panelLayout.x + panelLayout.width + xPadding);
	}

	if (graph.config.legendEnabled) {
		renderPanelLegend(panelElem, panel, panelLayout);
	}

	panel.config.series.forEach(s => {
		s.associatedScale = s.config.side === 'right' ? scaleRight : scaleLeft;
		renderSeries(clippedRegion, graph, panel, s, xScale, s.associatedScale);
	});

}

function getScale(panelLayout, tickValues) {
	const axisExtent = extent(tickValues);

	return scaleLinear()
		.domain(axisExtent)
		.range([
			panelLayout.y + panelLayout.height,
			panelLayout.y
		]);
}

function getTickValues(axis, side, height, dataExtent, fixedNumberOfTicks = null) {

	if (Array.isArray(axis.config.tickValues)) {
		return axis.config.tickValues;
	}

	const existingConstraints = axis.config.constraints || {};

	const constraints = fixedNumberOfTicks === null
		? existingConstraints
		: { ...existingConstraints, fixedNumberOfTicks };

	return calculateTickValues(dataExtent, height, constraints);

}

function getD3Axis(side, tickValues, scale, formatter) {
	return (side === 'left' ? axisLeft() : axisRight())
		.scale(scale)
		.tickValues(tickValues)
		.tickFormat(formatter)
		.tickSizeInner(5)
		.tickSizeOuter(0);
}



// WEBPACK FOOTER //
// ./utils/panelgraph/functions/render-panel.js